<template>
  <fin-selectable-card class="pa-6"
                       v-bind="cardOptions"
                       v-on="$listeners.click ? {click: (e) => $emit('click', e)} : {}">
    <fin-data-loader overlay :resolver="resolver" :data-adapter="adapter" :params="{selection, chartOptions}"
                     @input="change">
      <template #content>
        <v-row v-style:opacity="inactiveItem ? 0.5 : 1">
          <v-col cols="12" class="d-flex">
            <div class="card-title">
              <slot name="title">
                <template v-if="to">
                  <router-link class="card-title" style="text-decoration: none"
                               :to="to">
                    {{ title }}
                  </router-link>
                </template>
                <template v-else>
                  {{ title }}
                </template>
              </slot>
            </div>
            <fin-realtime-indicator v-if="(chartData.tradingPlatform || tradingPlatform) && !inactiveItem"
                                    :trading-platform="chartData.tradingPlatform || tradingPlatform"
                                    class="realtime"/>
          </v-col>
        </v-row>
        <v-row v-style:opacity="inactiveItem ? 0.5 : 1"
               :class="$vuetify.breakpoint.xs ? 'py-0 mt-0' : 'py-0 mt-4'">
          <v-col cols="12" md="auto" v-if="dropDownOptions && dropDownOptions.length>1">
            <v-select style="max-width: 176px"
                      v-model="selection"
                      item-value="id"
                      :items="dropDownOptions"
                      outlined
                      hide-details
                      dense
                      @input="userChange"
                      :disabled="inactiveItem"/>
          </v-col>

          <v-col v-if="!hideValues" v-style:opacity="inactiveItem ? 0.5 : 1"
                 :cols="$vuetify.breakpoint.xsOnly ? '12' : 'auto'">
            <div class="currentValue-title">
              <slot name="currentValue-title">
                <fin-lexicon-text auto-solve :text="chartData.currentValueTitle || currentValueTitle"/>
              </slot>
            </div>
            <div class="currentValue">
              <v-fade-transition leave-absolute>
                <fin-value-change :absolute="currentValue"
                                  :key="`current-${currentValue}`"
                                  :valueSuffix="valueSuffix"
                                  :noAutoColor="!currentValueUseSemanticColors"
                />
              </v-fade-transition>
            </div>
            <div>
              <fin-time-ago :datetime="changeDate" :limit="86400000" class="changeDate"/>
            </div>
          </v-col>

          <v-col v-if="performance" v-style:opacity="inactiveItem ? 0.5 : 1"
                 :cols="$vuetify.breakpoint.xsOnly ? '12' : 'auto'">
            <div class="currentValue-title">
              <fin-lexicon-text v-if="performance.from"
                                :text="chartData.prependPerformanceText || prependPerformanceText"
                                auto-solve/>
              {{ performance.longDescription }}
            </div>
            <div class="currentValue">
              <fin-value-change :absolute="performance.absolute"
                                :percent="hidePercentageChange ? null : performance.percent"
                                :valueSuffix="valueSuffix"
              />
            </div>
            <span v-if="performance.from" class="changeDate"> {{ performance.from | datetime }}</span>
          </v-col>
        </v-row>

        <v-row v-style:opacity="inactiveItem ? 0.5 : 1"
               class="pt-4">
          <v-col class="py-0 pl-3" cols="12">
            <div style="width: 100%">
              <fin-chart2 :options="chartData" ref="chart"/>
            </div>
          </v-col>
        </v-row>

        <template v-if="finCompare">
          <v-divider :class="$vuetify.breakpoint.xs ? 'my-0' : 'my-3'" horizontal/>

          <v-card-actions class="justify-center pa-0">
            <v-row>
              <v-col class="col-auto">
                <v-btn text
                       @click="showCompareDialog = true">
                  <v-icon>icon-plus</v-icon>
                  Vergleiche mit ...
                </v-btn>

                <fin-dialog v-model="showCompareDialog"
                            v-if="showCompareDialog"
                            :fullscreen="$vuetify.breakpoint.xsOnly"
                            :max-width="700"
                            disableScrollLock
                            :contentClass="`${keyboardShown ? 'keyboard-shown': ''}`">
                  <h2 class="dialog-title">
                    {{ compareTitle }}
                  </h2>
                  <fin-global-search ref="globalSearch"
                                     v-model="compareSelection"
                                     exclude-inactive
                                     :exclude-ids="excludeSelectedCompareIDs"/>

                  <template #actions>
                    <v-btn @click="closeCompareDialog">Zurück</v-btn>
                    <v-btn :disabled="!compareSelection"
                           class="ml-3"
                           dark
                           @click="selectCompare(compareSelection)">
                      Auswahl vergleichen
                    </v-btn>
                  </template>
                </fin-dialog>
              </v-col>
              <v-col>
                <v-chip v-for="(comp, index) in compare"
                        :color="comp.color"
                        :key="index"
                        @click:close="unselectCompare(comp)"
                        class="ml-3"
                        :close="!!finCompare.unselect"
                        label
                        outlined>
                  {{ comp.item.name }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card-actions>
        </template>
        <template v-if="$slots['card-actions']">
          <v-divider :class="$vuetify.breakpoint.xs ? 'my-0' : 'my-3'" horizontal/>
          <v-card-actions class="justify-center pa-0">
            <slot name="card-actions"/>
          </v-card-actions>
        </template>
      </template>
    </fin-data-loader>
  </fin-selectable-card>
</template>

<script>
import HighchartsCore from 'highcharts'
import FinChart2 from "@/components/common/Chart2/FinChart2"
import FinValueChange from "@/components/common/Chart2/PerformanceCharts/ValueChange"
import FinDataLoader from "@/components/common/DataLoader"
import FinSelectableCard from "@/components/common/Chart2/PerformanceCharts/SelectableCard"
import FinRealtimeIndicator from "@/components/common/RealtimeIndicator"
import FinLexiconText from "@/components/common/lexicon/LexiconText"
import FinGlobalSearch from "@/components/common/GlobalSearch"
import {EventBus} from "@/plugins/fincraft"
import AmplitudePush from "@/mixins/AmplitudePush"

function getY(point) {
  if (!point) {
    return 0
  }
  //TODO util
  if (Array.isArray(point)) {
    return point.length > 1 ? point[1] : 0
  }
  return point.y || 0
}

export default {
  name: "FinBigPerformanceChart",
  components: {
    FinGlobalSearch,
    FinLexiconText,
    FinRealtimeIndicator,
    FinSelectableCard,
    FinDataLoader,
    FinValueChange,
    FinChart2,
  },
  mixins: [AmplitudePush],
  props: {
    title: {
      type: String
    },
    currentValueTitle: {
      type: String,
      default: 'Aktueller Wert'
    },
    tradingPlatform: {
      type: String
    },
    prependPerformanceText: {
      type: String,
      default: 'Wertentwicklung seit'
    },
    chartOptions: {
      type: [Object, Array, Function, Promise]
    },
    cardOptions: {
      type: Object
    },
    hideValues: {
      type: Boolean
    },
    hidePercentageChange: {
      type: Boolean
    },
    currentValueUseSemanticColors: {
      type: Boolean,
      default: false
    },
    compareInitial: {
      type: Boolean,
      default: false
    },
    compareTitle: {
      type: String,
      default: 'Lege fest, womit du dein Portfolio vergleichen möchtest'
    },
    inactiveItem: {
      type: Boolean,
      default: false
    },
    to: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      performance: null,
      selection: 0,
      chartData: null,
      compare: [],
      showCompareDialog: this.compareInitial,
      compareSelection: null,
      keyboardShown: false,
      trackingOrigin: this.$route.name === 'myFinHub' ? 'finhub_portfolios' : 'fintools'
    }
  },
  computed: {
    excludeSelectedCompareIDs() {
      // add all previously selected ids
      const excludedIds = this.compare.map(c => c.item.id)
      // add the id of the current portfolio
      excludedIds.push(this.finCompare.excludePortfolioID)
      return excludedIds
    },
    isMulti() {
      return Array.isArray(this.chartOptions)
    },
    dropDownOptions() {
      return this.isMulti ? this.chartOptions.map(({title: text}, id) => ({
        text,
        id
      })) : []
    },
    valueSuffix() {
      return this.chartData?.tooltip?.valueSuffix || ''
    },
    currentValue() {
      let result = getY(this.performance?.end)
      if (this.chartData?.currentValue) {
        result = this.chartData.currentValue(result)
      }
      return result
    },
    changeDate() {
      return this.performance?.to ? new Date(this.performance.to) : null
    },
    finCompare() {
      return this.chartData?.finCompare
    },
    keepSelection() {
      return !!this.finCompare?.keepSelection
    },
  },
  methods: {
    closeCompareDialog() {
      this.showCompareDialog = false
      this.pushToAmplitude({
        'event': `fincraft_${this.trackingOrigin}_performanceanalyzer_compare_with`,
        'step': 'cancel_choice',
        'placement': `fincraft_${this.trackingOrigin}`,
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
    },
    userChange(val) {
      this.$emit('userChange', val)
    },
    resolver({selection}) {
      return this.isMulti ? this.chartOptions[selection].chart : this.chartOptions
    },
    adapter(val) {
      //clone in case it was a static returned value
      if (val?.series?.length) {
        val = HighchartsCore.merge({}, val)
        val.series[0].events = {
          performance: (props) => {
            this.performance = props
          }
        }
      }
      return val
    },
    change(val) {
      this.chartData = val
      if (this.keepSelection) {
        if (val.id !== -1) {
          const chart = this.$refs?.chart?.chart
          if (chart) {
            chart.showLoading()
            //TODO maybe move to resolver
            this.$nextTick(async () => {
              await this.finCompare.selectAll(chart, this.compare)
              chart.hideLoading()
            })
          }
        }
      }
    },
    async selectCompare(val, loading = true) {
      if (val) {
        val = JSON.parse(JSON.stringify(val))
        this.compareSelection = null
        const chart = this.$refs.chart.chart
        this.showCompareDialog = false
        chart.showLoading()
        // only add if it's the first element or if the id isn't already set
        if (this.compare.length === 0 || this.compare.find(c => c.item.id === val.item.id) === undefined) {
          const color = await this.finCompare.select(chart, val)
          val.color = color
          this.compare.push(val)
        }
        chart.hideLoading()

        const stepChoice = {
          type: val.typeLabel,
          choice: val.type === 'PORTFOLIO' ? null : val.item?.name
        }
        await this.pushToAmplitude({
          'event': `fincraft_${this.trackingOrigin}_performanceanalyzer_compare_with`,
          'step': 'confirm_choice',
          'step_choice': stepChoice,
          'placement': `fincraft_${this.trackingOrigin}`,
          'vue_breakpoint': this.$vuetify.breakpoint.name
        })
      }
    },
    unselectCompare(val) {
      this.compare.push(val)
      this.compare = this.compare.filter(c => c.item.id !== val.item.id)
      this.finCompare.unselect(this.$refs.chart.chart, val)
    },
  },
  watch: {
    chartOptions(val) {
      if (!this.keepSelection) {
        this.selection = 0
        this.compare = []
      }
      if (!this.keepSelection) this.selection = 0
    },
    selection(val) {
      this.compare = []
      this.$emit('input', val)
    },
    showCompareDialog: {
      immediate: true,
      handler(val) {
        if (val === true) {
          this.pushToAmplitude({
            'event': `fincraft_${this.trackingOrigin}_performanceanalyzer_compare_with`,
            'step': 'open_choice',
            'placement': `fincraft_${this.trackingOrigin}`,
            'vue_breakpoint': this.$vuetify.breakpoint.name
          })
        }
      }
    }
  },

  beforeDestroy() {
    EventBus.$off('mobileKeyboardShow')
    EventBus.$off('mobileKeyboardHide')
  },
  mounted() {
    EventBus.$on('mobileKeyboardShow', () => this.keyboardShown = true)
    EventBus.$on('mobileKeyboardHide', () => this.keyboardShown = false)
  }
}
</script>

<style scoped lang="scss">
/*Please do a styleguide*/

@media only screen and (min-width: 600px) {
  .dialog-title {
    padding-top: 18px;
    padding-bottom: 18px
  }
}

.dialog-title {
  font-weight: 700;
  font-size: 20px !important;
}

.card-title {
  font-family: Gilroy;
  font-size: 20px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  color: var(--night-blue);
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
}

.currentValue-title {
  font-family: Gilroy;
  font-size: 12px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.17;
  letter-spacing: 1px;
  color: var(--night-blue);
}

.currentValue {
  font-family: Gilroy;
  font-size: 28px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.19;
  letter-spacing: normal;
  color: var(--night-blue);
}

.valueChange {
  font-family: Gilroy;
  font-size: 16px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.38;
  letter-spacing: normal;
}

.changeDate {
  font-family: Gilroy;
  font-size: 12px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.25;
  letter-spacing: normal;
  color: var(--grey-7);
}

.realtime {
  margin-top: 3px;
  margin-left: 3px;
  vertical-align: top;
  min-width: 64.25px;
}
</style>
